'use client';

import { useState } from 'react';
import { dataLayerPush, trackActivateReward } from '@libs/analytics';
import { activateOffer, getOffers as fetchMemberOffers } from '@libs/api-proxy-client';
import { ACTIVATE_OFFER_POST_LOGIN_ACTION, logSentryError, OPEN_OFFER_DETAILS_POST_LOGIN_ACTION, type PostLoginIntent, SentryAcpTreatsRewardsErrors as SentryErrors, useAlertBanner, useAuthSignInClick, useDecisions, useLoyaltyContext, useOptimizely, usePageLayout } from '@libs/common-next';
import type { Offer } from '@libs/common-types';
import { ensureAuth0AccessToken, evaluateSession, isUserLoggedIn } from '@libs/core';
import { SnackbarUtils } from '@libs/shared-ui/utils';
import { applyMemberState } from '../../utils/apply-member-state';
import { getActivateRewardSelection } from './autoshipPointsPayoutTracking';
import { type OfferPageSection, useOfferDrawerStore } from './useOfferDrawerStore';
export function getCardSize(offer?: Offer, isFeatured?: boolean) {
  if (isFeatured) {
    return 'featured';
  }
  return offer?.cardSize.toLowerCase() === 'large' ? 'large' : 'small';
}
export function useOfferCardActions() {
  const {
    featuredOffers,
    getOffers,
    isActivating,
    offers
  } = useLoyaltyContext();
  const {
    drawerOpen,
    selectedOffer,
    offerPageSection,
    openOffer,
    closeOffer,
    markOfferActivated
  } = useOfferDrawerStore();
  const {
    isAuthenticated,
    user
  } = usePageLayout();
  const {
    handleSignInClick
  } = useAuthSignInClick();
  const isAsPointsPayoutCtaEnabled = useDecisions('as_pts_pyt_cta');
  const {
    setAlert
  } = useAlertBanner();
  const {
    context
  } = useOptimizely();
  const [activatingOffers, setActivatingOffers] = useState<Set<string>>(new Set());
  function handleCardClick(offer: Offer, pageSection?: OfferPageSection) {
    openOffer(offer, pageSection);
    context?.trackEvent('offer_details_modal_click');
    // Reset error so pre-existing error doesn't display inside offer drawer
    setAlert(undefined);
  }
  function handleCloseDrawer() {
    closeOffer();

    // Reset error so error from offer drawer doesn't display on offer grid
    setAlert(undefined);
  }
  async function handleActivateOffer(offer_0: Offer | undefined, gtmSection: OfferPageSection, actionAfterLogin: PostLoginIntent['action'] = OPEN_OFFER_DETAILS_POST_LOGIN_ACTION) {
    if (!offer_0?.isActivatable || offer_0.isActivated) {
      return;
    }
    const shouldActivate = actionAfterLogin === ACTIVATE_OFFER_POST_LOGIN_ACTION;
    if (!isAuthenticated && !user) {
      const postLoginIntent: PostLoginIntent = {
        action: actionAfterLogin,
        params: {
          offerId: offer_0.offerId,
          gtmSection
        }
      };
      handleSignInClick({
        postLoginIntent,
        legacyDrawerProps: {
          onSuccess: () => handleOfferSignInSuccess(offer_0, shouldActivate, gtmSection),
          onRegisterClick: () => {
            try {
              sessionStorage.setItem('registration:postLoginIntent', JSON.stringify(postLoginIntent));
            } catch {
              return;
            }
          }
        }
      });
      return;
    }
    await activateAuthenticatedOffer(offer_0, gtmSection);
  }
  async function handleOfferSignInSuccess(offer_1: Offer | string, shouldActivate_0: boolean, gtmSection_0?: OfferPageSection) {
    try {
      const response = await fetchMemberOffers();
      if (!response) {
        throw new Error('Failed to fetch offers after sign-in');
      }
      if (!isUserLoggedIn()) return;
      const offerId = typeof offer_1 === 'string' ? offer_1 : offer_1.offerId;
      const memberOffer = response.offers.find(candidate => candidate.offerId === offerId);
      if (!memberOffer) return;
      const displayOffer = typeof offer_1 === 'string' ? offers?.find(candidate_1 => candidate_1.offerId === offerId) ?? featuredOffers?.find(candidate_0 => candidate_0.offerId === offerId) ?? memberOffer : offer_1;
      const offerWithMemberState = applyMemberState(displayOffer, memberOffer);
      openOffer(offerWithMemberState, gtmSection_0);
      if (shouldActivate_0) {
        await activateAuthenticatedOffer(offerWithMemberState, gtmSection_0);
      }
    } catch (error) {
      SnackbarUtils.showPredefined('SERVER_ERROR');
      logSentryError(error, SentryErrors.OfferCard.ActivateOfferFailed);
    }
  }
  async function activateAuthenticatedOffer(offer_2: Offer, gtmSection_1?: OfferPageSection) {
    if (!offer_2.isActivatable || offer_2.isActivated) return;
    const activateOfferIdFromUrl = typeof window === 'undefined' ? null : new URLSearchParams(window.location.search).get('activateOfferId');
    setActivatingOffers(prevOffers => new Set(prevOffers).add(offer_2.offerId));
    try {
      const sessionStillValid = await evaluateSession({});
      if (!sessionStillValid) {
        return;
      }
      await ensureAuth0AccessToken();
      if (!isUserLoggedIn()) {
        return;
      }
      const response_0 = await activateOffer(offer_2);
      if (response_0 && 'success' in response_0 && response_0?.success) {
        setAlert(undefined);
        context?.trackEvent('offer_activations');
        if (gtmSection_1) {
          trackActivateReward({
            pageSection: gtmSection_1,
            selection: getActivateRewardSelection({
              offer: offer_2,
              isAsPointsPayoutCtaEnabled,
              activateOfferIdFromUrl
            })
          });
        }

        // Update selectedOffer immediately to reflect activated state
        markOfferActivated(offer_2.offerId);
        // Fetch offers again from API to get updated data after activation
        await getOffers();
      } else {
        throw new Error(`Failed to activate an offer, offerId: ${offer_2.offerId}`);
      }
    } catch (error_0) {
      SnackbarUtils.showPredefined('SERVER_ERROR');
      dataLayerPush({
        event: 'error',
        site_section: gtmSection_1 || 'treats rewards',
        error_message: `uh oh! [${offer_2.shortDescription}] order didn't activate. please try again.`
      });
      logSentryError(error_0, SentryErrors.OfferCard.ActivateOfferFailed);
    } finally {
      setActivatingOffers(prevOffers => {
        const updatedSet = new Set(prevOffers);
        updatedSet.delete(offer_2.offerId);
        return updatedSet;
      });
    }
  }
  function showSpinner(offer_3?: Offer) {
    return offer_3 ? activatingOffers.has(offer_3.offerId) : false;
  }
  return {
    drawerOpen,
    handleActivateOffer,
    handleCardClick,
    handleCloseDrawer,
    handleOfferSignInSuccess,
    isActivating,
    offerPageSection,
    selectedOffer,
    showSpinner
  };
}